import moment from 'moment';

// Clients explicitly hidden from operational shift views (case-insensitive
// match on entity.name). Nurse Ready is an internal pipeline, not a real shift
// to act on, so it is filtered out of the calendar and unfilled shift lists.
export const HIDDEN_CLIENT_NAMES = ['nurse ready'];

export const isHiddenClientEntity = entity =>
    HIDDEN_CLIENT_NAMES.includes((entity?.name || '').trim().toLowerCase());

// Temporarily hidden from the general Unfilled list only: Occupational
// Therapist shifts for VHA Home Healthcare & Champlain Rehab Solutions.
const HIDDEN_CLIENT_SKILLS = {
    'vha home healthcare & champlain rehab solutions': [
        'occupational therapist',
    ],
};

export const isHiddenClientSkillShift = shift => {
    const clientName = (shift?.entity?.name || '').trim().toLowerCase();
    const skill = (shift?.skill || '').trim().toLowerCase();
    return (HIDDEN_CLIENT_SKILLS[clientName] || []).includes(skill);
};

export const isDateExistsInRecurringShift = (_date, shiftData) => {
    if (
        shiftData.shiftType !== 'recurring' ||
        !shiftData?.shiftDate ||
        !shiftData?.repeatOn?.length
    ) {
        return false;
    }
    const date = new Date(_date);
    const shiftDate = new Date(
        moment(shiftData.shiftDate).format('YYYY-MM-DD'),
    );
    const shiftDay = moment(date).format('dddd');
    if (
        date >= shiftDate &&
        shiftData.repeatOn.includes(shiftDay) &&
        moment(date).diff(moment(shiftDate), 'weeks') %
            shiftData?.repeatEvery ===
            0
    ) {
        return true;
    }
    return false;
};

export const extractHoursFromShiftTime = shiftTime => {
    return parseInt(shiftTime);
};

export const extractMinutesFromShiftTime = shiftTime => {
    const mins = shiftTime - extractHoursFromShiftTime(shiftTime);
    if (mins === 0.25) {
        return 15;
    } else if (mins === 0.5) {
        return 30;
    } else if (mins === 0.75) {
        return 45;
    }
    return 0;
};

/**
 * Formats transit allowance display with proper singular/plural formatting
 * @param {number|string} value - The transit allowance value in hours
 * @returns {string} Formatted transit allowance string
 */
export const formatTransitAllowanceDisplay = value => {
    if (!value || value === '0' || value === 0) return 'N/A';

    const numValue = parseFloat(value);
    if (numValue === 0.5) return '30 minutes';
    if (numValue === 1) return '1 hour';

    return `${numValue} hours`;
};

/**
 * Calculates the total time worked including breaks for a given tracker
 * @param {Object} tracker - The tracker object containing start, end, breaks, and state
 * @returns {number} Total time worked in milliseconds
 */
export const calculateTotalTimeWorkedWithPause = tracker => {
    if (!tracker) return 0;

    // Create a copy to avoid modifying the original object
    const trackerCopy = { ...tracker };
    trackerCopy.breaks = (tracker.breaks || []).filter(
        unpaidBreak => unpaidBreak.end !== 0,
    );

    if (trackerCopy.end) {
        return calculateCompletedShiftTime(trackerCopy);
    } else {
        return calculateOngoingShiftTime(trackerCopy);
    }
};

/**
 * Calculates time for a completed shift (has end time)
 * @param {Object} tracker - The tracker object
 * @returns {number} Time in milliseconds
 */
const calculateCompletedShiftTime = tracker => {
    if (!tracker.breaks.length) {
        return moment(tracker.end * 1000).diff(
            tracker.start * 1000,
            'milliseconds',
        );
    }

    if (tracker.breaks[0].end === 0) {
        return moment(tracker.end * 1000).diff(
            tracker.start * 1000,
            'milliseconds',
        );
    }

    let time = 0;

    // Add time from last break end to shift end
    if (tracker.breaks[tracker.breaks.length - 1].end) {
        time += moment(tracker.end * 1000).diff(
            tracker.breaks[tracker.breaks.length - 1].end * 1000,
            'milliseconds',
        );
    }

    // Add time between breaks
    for (let i = tracker.breaks.length - 1; i >= 0; i--) {
        if (i === 0) {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.start * 1000,
                'milliseconds',
            );
        } else {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.breaks[i - 1].end * 1000,
                'milliseconds',
            );
        }
    }

    return time;
};

/**
 * Calculates time for an ongoing shift (no end time)
 * @param {Object} tracker - The tracker object
 * @returns {number} Time in milliseconds
 */
const calculateOngoingShiftTime = tracker => {
    if (!tracker.breaks.length) {
        return moment().diff(tracker.start * 1000, 'milliseconds');
    }

    let time = 0;

    // If tracker is in 'played' state, add time from last break end to now
    if (tracker.state === 'played') {
        time += moment().diff(
            tracker.breaks[tracker.breaks.length - 1].end * 1000,
            'milliseconds',
        );
    }

    // Add time between breaks
    for (let i = tracker.breaks.length - 1; i >= 0; i--) {
        if (i === 0) {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.start * 1000,
                'milliseconds',
            );
        } else {
            time += moment(tracker.breaks[i].start * 1000).diff(
                tracker.breaks[i - 1].end * 1000,
                'milliseconds',
            );
        }
    }

    return time;
};

/**
 * Formats milliseconds to HH:MM:SS format
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} Formatted time string (e.g., "8:30:45")
 */
export const formatTimeFromMilliseconds = milliseconds => {
    if (!milliseconds || milliseconds < 0) return '00:00:00';

    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Checks if invoice editing is restricted due to Fiscal Year-End October 2024
 * Works for a single job or for an invoice containing multiple jobs.
 * @param {Object} invoiceOrJob - Job object or invoice containing jobs
 * @returns {boolean} True if editing is restricted
 */
export const checkCuttOfDates = invoiceOrJob => {
    if (!invoiceOrJob) {
        return false;
    }

    const isInvoice =
        Array.isArray(invoiceOrJob.jobs) && invoiceOrJob.jobs.length > 0;

    const cutoffDateForZone = timeZone =>
        moment
            .tz(timeZone || 'America/Toronto')
            .year(2024)
            .month('October')
            .endOf('month')
            .endOf('day');

    const isJobBeforeCutoff = jobItem => {
        if (!jobItem || !jobItem.shiftStartTime) {
            return false;
        }
        const tz = jobItem.timeZone || 'America/Toronto';
        const jobDate = moment.unix(jobItem.shiftStartTime).tz(tz);
        return jobDate.isBefore(cutoffDateForZone(tz));
    };

    if (isInvoice) {
        return invoiceOrJob.jobs.some(jobObj =>
            isJobBeforeCutoff(jobObj.job || jobObj),
        );
    }

    return isJobBeforeCutoff(invoiceOrJob);
};

/**
 * Calculates the minimum payment hours for a job
 * @param {number} minimumShiftHours - The minimum shift hours
 * @param {string} unit - The unit type ('hour' or 'day')
 * @returns {number} Minimum hours required
 */
export const minimumPaymentForTheJob = (minimumShiftHours, unit) => {
    if (unit === 'hour') {
        return minimumShiftHours || 4;
    }
    return 8; // Default for day shifts
};

/**
 * Validates tracker time format
 * @param {string} timeString - Time in HH:mm:ss format
 * @returns {Object} Validation result with isValid and error message
 */
export const validateTrackerTime = timeString => {
    if (!timeString) {
        return { isValid: false, error: 'Time is required' };
    }

    // Validate time format (HH:mm:ss)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    if (!timeRegex.test(timeString)) {
        return { isValid: false, error: 'Invalid time format. Use HH:mm:ss' };
    }

    // Parse time and check minimum hours
    // const duration = moment.duration(timeString);
    // const hours = duration.asHours();
    // const minHours = minimumPaymentForTheJob(minimumShiftHours, 'hour');

    // if (hours < minHours) {
    //     return {
    //         isValid: false,
    //         error: `Tracker should be greater than ${minHours} hours`,
    //     };
    // }

    return { isValid: true, error: null };
};

/**
 * Calculates shift end time based on start time and duration
 * @param {number} startTime - Unix timestamp of shift start
 * @param {string} duration - Duration in HH:mm:ss format
 * @returns {number} Unix timestamp of shift end
 */
export const calculateShiftEndTime = (startTime, duration) => {
    const durationSeconds = moment.duration(duration).asSeconds();
    return startTime + durationSeconds;
};

/**
 * Generates available experience options: 1-12 months, then 2-10 years
 * @returns {Array<{label: string, value: number}>} List of experience options
 */
export const getAvailableExperienceOptions = () => {
    const options = [];
    // 1-12 months
    for (let i = 1; i <= 12; i++) {
        options.push({ label: `${i} Month${i > 1 ? 's' : ''}`, value: i });
    }
    // 2-10 years (converted to months)
    for (let i = 2; i <= 10; i++) {
        options.push({ label: `${i} Years`, value: i * 12 });
    }
    return options;
};
